import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Pressable, Text, VStack } from 'native-base'
import { FormControl, IInputProps, Input } from 'native-base'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { logError } from '../../../../utils/monitorClient'
import { colors } from '../../../../utils/theme/configureTheme'

export interface IAddressReturnValue {
  street_line?: string
  secondary?: string
  city?: string
  state?: string
  zipcode?: string
}

export interface IAutofillTextInput extends IInputProps {
  label?: string
  leftIcon?: ReactNode
  placeholder?: string
  // function to call when a suggestion is selected
  onPlaceSelect: (suggestion: IAddressReturnValue) => void
  optional?: boolean
  name: string
  clearable?: boolean
  // if true, will clear the input value when the input is blurred
  clearOnBlur?: boolean
  handleBlur?: (val: string, val2: string) => void
  // if true, will disable the input
  isDisabled?: boolean
  // in milliseconds
  debounceDelay?: number
  // if true, will use autocomplete to get suggestions
  useAutocomplete?: boolean
  // if useAutocomplete is true, will check the length of the input text and if it is greater than the autoCompleteStringCheckLength, will use autocomplete to get suggestions
  autoCompleteStringCheckLength?: number
}

const DEFAULT_DEBOUNCE_WAIT = 1000 //in millis

const AutofillTextInput = ({
  placeholder,
  onPlaceSelect,
  label,
  leftIcon,
  isDisabled,
  optional = false,
  name,
  clearable,
  debounceDelay,
  useAutocomplete = true,
  autoCompleteStringCheckLength = 5,
  ...props
}: IAutofillTextInput) => {
  const { t } = useTranslation()
  const [, meta, helpers] = useField<string>(name)
  const { isSubmitting } = useFormikContext()
  // eslint-disable-next-line
  let inputRef = useRef<any | null>(null)
  const [suggestionsAvailable, setSuggestionsAvailable] = useState(false)
  const [searchValue, setSearchValue] = useState(meta.value)
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    let mounted = true
    if (mounted) setSearchValue(meta.value)
    return () => {
      mounted = false
    }
  }, [meta.value])

  const getSuggestions = useCallback(
    async (inputText: string) => {
      if (inputText?.length && inputText?.length >= autoCompleteStringCheckLength) {
        try {
          const embeddedKey = import.meta.env.REACT_APP_SMARTY_EMBEDDED_KEY
          const endpoint = `https://us-autocomplete-pro.api.smarty.com/lookup?search=${inputText}&key=${embeddedKey}&max_results=3`
          const response = await fetch(endpoint, {
            headers: { Referer: 'www.shipveho.com' },
          })
          const results = await response.json()
          if (results?.suggestions?.length) {
            setSuggestions(results?.suggestions)
          }
        } catch (err) {
          logError('Error fetching data for autocomplete', { error: err })
        }
      }
    },
    [autoCompleteStringCheckLength]
  )

  const debounceGetSuggestions = useDebouncedCallback((textVal: string) => {
    getSuggestions(textVal)
  }, debounceDelay ?? DEFAULT_DEBOUNCE_WAIT)

  const debouncedOnChangeText = useCallback(
    (inputVal: string) => {
      setSearchValue(inputVal)
      if (useAutocomplete) {
        debounceGetSuggestions(inputVal)
      }
      // set the value for formik field regardless of suggestion result
      helpers.setValue(inputVal)
    },
    [debounceGetSuggestions, helpers, useAutocomplete]
  )

  const handleSuggestionOnPress = useCallback(
    (suggestion: IAddressReturnValue) => {
      if (suggestion.street_line) {
        setSearchValue(suggestion.street_line)
        helpers.setValue(suggestion.street_line)
        onPlaceSelect(suggestion)
        setSuggestions([])
      }
    },
    [helpers, onPlaceSelect]
  )

  return (
    <FormControl
      isInvalid={meta.error?.length ? true : false}
      testID={`${name}-form-control`}
      bg={'transparent'}
      style={{ width: '100%' }}
    >
      <VStack direction="row">
        {label && (
          <FormControl.Label
            testID={`${name}-label`}
            _text={{
              fontWeight: 600,
              fontSize: 13,
              color: colors.brand.softBlack,
            }}
          >
            {label}
          </FormControl.Label>
        )}
        {optional && (
          <FormControl.Label
            testID={`${name}-optional-label`}
            ml={3}
            _text={{
              fontWeight: 700,
              fontSize: 11,
              color: isDisabled ? colors.gray[200] : colors.brand.darkGrey,
              alignSelf: 'center',
              textTransform: 'uppercase',
            }}
          >
            Optional
          </FormControl.Label>
        )}
      </VStack>
      <Input
        width="full"
        bg={colors.brand.white}
        _focus={{
          bg: colors.brand.white,
          borderColor: colors.brand.softBlack,
        }}
        ref={inputRef}
        size="lg"
        isDisabled={isDisabled || isSubmitting}
        onFocus={() => setSuggestionsAvailable(true)}
        // need to stop onBlur event prop to allow onPress for suggestions to fire first
        // eslint-disable-next-line
        onBlur={(event: any) => {
          setTimeout(() => {
            event?.stopPropagation()
            setSuggestionsAvailable(false)
          }, 200)
        }}
        InputLeftElement={leftIcon}
        onChangeText={(val: string) => debouncedOnChangeText(val as string)}
        borderColor={colors.brand.darkGrey}
        borderWidth={2}
        value={searchValue}
        testID={`${name}-text-input`}
        {...props}
        isInvalid={!!meta.error}
        placeholder={placeholder ?? meta.value}
        autocomplete={'address-line1'}
      />
      {meta.error && (
        <FormControl.ErrorMessage fontSize={12} testID={`${name}-error`}>
          {meta.error}
        </FormControl.ErrorMessage>
      )}
      {suggestions?.length > 0 && searchValue && suggestionsAvailable && (
        <VStack
          style={{
            backgroundColor: colors.brand.lightGrey,
            borderRadius: 4,
            padding: 4,
            marginTop: 4,
            alignItems: 'center',
            paddingHorizontal: 20,
            paddingTop: 10,
            width: '110%',
            alignSelf: 'center',
          }}
        >
          {suggestions.map((suggestion: IAddressReturnValue, index) => {
            return (
              <Pressable
                key={index}
                onPress={() => handleSuggestionOnPress(suggestion)}
                style={{ backgroundColor: 'white', alignSelf: 'stretch' }}
              >
                <VStack px={'10px'} borderBottomWidth={'1px'} borderBottomColor={colors.brand.lightGrey}>
                  <Text>{`${suggestion?.street_line} ${suggestion?.secondary}`}</Text>
                  <Text>{`${suggestion?.city}, ${suggestion?.state} ${suggestion?.zipcode}`}</Text>
                </VStack>
              </Pressable>
            )
          })}
          {clearable && (
            <Pressable
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onPress={() => setSuggestions([])}
            >
              <Text>{t('common:CLEAR')}</Text>
            </Pressable>
          )}
        </VStack>
      )}
    </FormControl>
  )
}

export default AutofillTextInput
